import { Link } from 'react-router-dom';
import { ArrowRight, BarChart3 } from 'lucide-react';

import { SoftCard } from '@/components/ui/soft-card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useShopPath } from '@/hooks/useShopPath';
import { formatPct } from '@/lib/format-pct';

/**
 * Profile completeness ladder. Each row maps a product attribute (title,
 * image, description, brand, category) to a completion %. Each row has
 * a CTA that deep-links into Manage Items pre-filtered to that issue.
 *
 * The "details", "multi-images" and "AI/SEO optimised" tiers from Lovable
 * are intentionally omitted in this first cut — they need product-data
 * shapes (allergens / dimensions / weight / image-count / AI-optimisation
 * flag) that aren't yet faceted in Typesense. Tracked as a backend follow-up.
 */

const TIERS = [
	{ tier: 'minimum', label: 'Minimum', attributes: ['title'] },
	{ tier: 'complete', label: 'Complete', attributes: ['image', 'description'] },
	{ tier: 'great', label: 'Great', attributes: ['brand', 'category'] }
];

const TIER_COLOR = {
	minimum: 'bg-warning',
	complete: 'bg-action',
	great: 'bg-success'
};

const ATTR_META = {
	title: { label: 'Title', issue: 'missing-title' },
	image: { label: 'Image', issue: 'missing-image' },
	description: { label: 'Description', issue: 'missing-description' },
	brand: { label: 'Brand', issue: 'missing-brand' },
	category: { label: 'Category', issue: 'missing-category' }
};

export function ProfileCompletenessBar({ profileCompleteness = [] }) {
	const { shopPath } = useShopPath();
	const byAttr = Object.fromEntries(profileCompleteness.map((s) => [s.attribute, s]));

	const rows = TIERS.flatMap((tier) =>
		tier.attributes
			.map((attribute) => {
				const seg = byAttr[attribute];
				if (!seg) return null;
				const meta = ATTR_META[attribute];
				// Compute missing pct from raw counts rather than 100 - rounded-pct,
				// so a sliver (e.g. 23 of 9,454) reads as "<1%" instead of "0%".
				const totalForAttribute = (seg.presentCount ?? 0) + (seg.missingCount ?? 0);
				const missingPctLabel = formatPct(seg.missingCount ?? 0, totalForAttribute);
				return (
					<li key={attribute} className="flex items-center gap-2.5">
						<div className="min-w-0 flex-1">
							<div className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold text-foreground">
								<span className="truncate">{meta.label}</span>
							</div>
							<Tooltip>
								<TooltipTrigger asChild>
									<div className="relative h-2 w-full cursor-default overflow-hidden rounded-full bg-muted">
										<div
											className={`${TIER_COLOR[tier.tier]} absolute inset-y-0 left-0 rounded-full transition-opacity hover:opacity-80`}
											style={{ width: `${seg.pct}%` }}
											aria-label={`${meta.label}: ${seg.pct}%`}
										/>
									</div>
								</TooltipTrigger>
								<TooltipContent side="top" className="text-xs">
									<div className="font-semibold">
										{meta.label} · {seg.pct}%
									</div>
									<div className="text-muted-foreground">{tier.label} tier</div>
								</TooltipContent>
							</Tooltip>
						</div>
						<div className="w-28 shrink-0 text-right tabular-nums">
							<div className="text-sm font-bold text-foreground">{seg.missingCount.toLocaleString()}</div>
							<div className="text-[11px] text-muted-foreground">{missingPctLabel} incomplete</div>
						</div>
						<Link
							to={shopPath(`/inventory/manage?issue=${meta.issue}`)}
							className="ml-3 inline-flex shrink-0 items-center gap-1.5 rounded-full bg-primary px-3.5 py-2 text-xs font-semibold text-primary-foreground transition hover:bg-primary/90"
						>
							Fix <ArrowRight className="h-3 w-3" />
						</Link>
					</li>
				);
			})
			.filter(Boolean)
	);

	if (rows.length === 0) return null;

	return (
		<SoftCard className="!p-5 md:!p-6">
			<div className="mb-4">
				<div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
					<BarChart3 className="h-3.5 w-3.5" /> Product profile completeness
				</div>
			</div>

			<TooltipProvider delayDuration={150}>
				<ul className="space-y-3">{rows}</ul>
			</TooltipProvider>
		</SoftCard>
	);
}
